import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import {
  ArrowLeft,
  Building2,
  Settings,
  Edit,
  Save,
  X,
  Eye,
  EyeOff,
  Activity,
} from "lucide-react";
import {
  type Company,
  type UpdateCompanyData,
  companyApi,
} from "../services/companyApi";

import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader } from "@/components/Loader";

export function CompanyDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [settingLoading, setSettingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [isPreviewingSystemPrompt, setIsPreviewingSystemPrompt] =
    useState(true);
  const [isPreviewingPromptFoundation, setIsPreviewingPromptFoundation] =
    useState(true);

  // Form state for settings
  const [formData, setFormData] = useState({
    systemPrompt: "",
    promptFoundation: "",
    assignedTools: [] as string[],
    newTool: "",
    voice: "",
    model: "",
    greetOnConnectEnabled: true,
  });

  const queryClient = useQueryClient();
  const {
    data: company,
    isLoading: loading,
    error: companyError,
  } = useQuery<Company, Error>({
    queryKey: ["company", id],
    queryFn: () => companyApi.getCompany(id as string),
    enabled: Boolean(id),
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (company) {
      setFormData({
        systemPrompt: company.systemPrompt || "",
        promptFoundation: company.promptFoundation || "",
        assignedTools: [...(company.assignedTools || [])],
        newTool: "",
        voice: company.voice || "",
        model: company.model || "",
        greetOnConnectEnabled: company.greetOnConnectEnabled ?? true,
      });
      if (
        !company.systemPrompt &&
        !company.promptFoundation &&
        (!company.assignedTools || company.assignedTools.length === 0)
      ) {
        setIsEditingSettings(true);
      }
    }
  }, [company]);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyData }) =>
      companyApi.updateCompany(id, data),
    onSuccess: async () => {
      setIsEditingSettings(false);
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["companies"] }),
        queryClient.invalidateQueries({ queryKey: ["company", id] }),
      ]);
    },
    onSettled: () => setSettingLoading(false),
  });

  const handleSaveSettings = async () => {
    if (!id || !company) return;
    setSettingLoading(true);
    const updateData: UpdateCompanyData = {
      name: company.name,
      email: company.email,
      phone: company.phone,
      systemPrompt: formData.systemPrompt || undefined,
      promptFoundation: formData.promptFoundation || undefined,
      assignedTools: formData.assignedTools,
      voice: formData.voice || undefined,
      model: formData.model || undefined,
      greetOnConnectEnabled: formData.greetOnConnectEnabled,
    };
    await updateMutation
      .mutateAsync({ id, data: updateData })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Failed to save settings")
      );
  };

  const handleAddTool = () => {
    if (
      formData.newTool.trim() &&
      !formData.assignedTools.includes(formData.newTool.trim())
    ) {
      setFormData({
        ...formData,
        assignedTools: [...formData.assignedTools, formData.newTool.trim()],
        newTool: "",
      });
    }
  };

  const handleRemoveTool = (index: number) => {
    setFormData({
      ...formData,
      assignedTools: formData.assignedTools.filter((_, i) => i !== index),
    });
  };

  const resetForm = () => {
    if (company) {
      setFormData({
        systemPrompt: company.systemPrompt || "",
        promptFoundation: company.promptFoundation || "",
        assignedTools: [...(company.assignedTools || [])],
        voice: company.voice || "",
        model: company.model || "",
        newTool: "",
        greetOnConnectEnabled: company.greetOnConnectEnabled ?? true,
      });
    } else {
      setFormData({
        systemPrompt: "",
        promptFoundation: "",
        assignedTools: [],
        newTool: "",
        voice: "",
        model: "",
        greetOnConnectEnabled: true,
      });
    }
  };

  const startEditing = () => {
    resetForm();
    setIsEditingSettings(true);
    setIsPreviewingSystemPrompt(true);
    setIsPreviewingPromptFoundation(true);
  };

  const cancelEditing = () => {
    resetForm();
    setIsEditingSettings(false);
    setIsPreviewingSystemPrompt(true);
    setIsPreviewingPromptFoundation(false);
  };

  if (loading) {
    return <Loader />;
  }

  if (companyError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-red-500">Error: {companyError.message}</p>
        <Button onClick={() => navigate("/admin/companies")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Companies
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <Building2 className="h-8 w-8 mr-3 text-gray-600" />
              {company?.name}
            </h1>
            <p className="text-gray-500">Company Details & Settings</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link to={`/admin/companies/${company?.id}/sessions`}>
            <Button variant="outline">
              <Activity className="h-4 w-4 mr-2" />
              View Sessions
            </Button>
          </Link>
        </div>
      </div>

      {(error || companyError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {(error || companyError)?.toString()}
        </div>
      )}

      {/* Company Information */}
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Company Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Company Name
              </Label>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                {company?.name}
              </p>
            </div>

            {company?.address && (
              <div>
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Address
                </Label>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  <p>{company?.address.line1}</p>
                  {company?.address.line2 && <p>{company?.address.line2}</p>}
                  <p>
                    {company?.address.city}, {company?.address.state}{" "}
                    {company?.address.postalCode}
                  </p>
                  {company?.address.country && (
                    <p>{company?.address.country}</p>
                  )}
                </div>
              </div>
            )}
            {company?.email && (
              <div>
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Email
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {company.email}
                </p>
              </div>
            )}
            {company?.number && (
              <div>
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Assigned Number ({company.number.source})
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {company.number.friendlyName}
                </p>
              </div>
            )}

            {company?.phone && (
              <div>
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  Contact Phone
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {company.phone}
                </p>
              </div>
            )}
          </div>

          {company?._count && (
            <div className="mt-6">
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3 block">
                Statistics
              </Label>
              <div className="flex space-x-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {company._count.users}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-300">
                    Users
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {company._count.orders}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-300">
                    Orders
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {company._count.products}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-300">
                    Products
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {company._count.sessions}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-300">
                    Sessions
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Settings */}
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Company Settings
            </CardTitle>

            <div className="flex space-x-2">
              {company &&
              (company.systemPrompt ||
                company.promptFoundation ||
                (company.assignedTools && company.assignedTools.length > 0)) &&
              !isEditingSettings ? (
                <Button variant="outline" onClick={startEditing}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <>
                  {company &&
                    (company.systemPrompt ||
                      company.promptFoundation ||
                      (company.assignedTools &&
                        company.assignedTools.length > 0)) && (
                      <Button variant="outline" onClick={cancelEditing}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                  <Button
                    onClick={handleSaveSettings}
                    disabled={settingLoading}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {settingLoading ? "Saving..." : "Save"}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-hidden">
          <div className="space-y-6">
            {!company ||
            isEditingSettings ||
            (!company.systemPrompt &&
              !company.promptFoundation &&
              (!company.assignedTools ||
                company.assignedTools.length === 0)) ? (
              // Edit mode
              <div className="space-y-4">
                {!company?.systemPrompt &&
                  !company?.promptFoundation &&
                  (!company?.assignedTools ||
                    company.assignedTools.length === 0) && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800">
                        <strong>Configure Company Settings</strong> - Set up
                        system prompts and tools for this company.
                      </p>
                    </div>
                  )}

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      Model
                    </label>
                    <Select
                      value={formData.model}
                      onValueChange={(value) =>
                        setFormData({ ...formData, model: value })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "gpt-4o-mini-realtime-preview",
                          "gpt-4o-realtime-preview",
                          "gpt-realtime",
                        ].map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none">
                      Voice
                    </label>
                    <Select
                      value={formData.voice}
                      onValueChange={(value) =>
                        setFormData({ ...formData, voice: value })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select voice" />
                      </SelectTrigger>
                      <SelectContent>
                        {[
                          "marin",
                          "cedar",
                          "alloy",
                          "ash",
                          "ballad",
                          "coral",
                          "echo",
                          "sage",
                          "shimmer",
                          "verse",
                        ].map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Assigned Tools</Label>
                    <div className="flex space-x-2 mb-2">
                      <Input
                        placeholder="Add tool name..."
                        value={formData.newTool}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            newTool: e.target.value,
                          })
                        }
                        onKeyPress={(e) => e.key === "Enter" && handleAddTool()}
                      />
                      <Button type="button" onClick={handleAddTool} size="sm">
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.assignedTools.map((tool, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center space-x-1"
                        >
                          <span>{tool}</span>
                          <button
                            onClick={() => handleRemoveTool(index)}
                            className="ml-1 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Settings Checkboxes */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="greetOnConnectEnabled"
                      checked={formData.greetOnConnectEnabled}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          greetOnConnectEnabled: checked === true,
                        })
                      }
                    />
                    <Label
                      htmlFor="greetOnConnectEnabled"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Enable greeting on connect
                    </Label>
                  </div>
                </div>

                <div>
                  <Label htmlFor="systemPrompt">System Prompt</Label>
                  <textarea
                    id="systemPrompt"
                    className="w-full min-h-[300px] p-3 border border-gray-300 rounded-md resize-y"
                    placeholder="Enter system prompt using markdown..."
                    value={formData.systemPrompt}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        systemPrompt: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="promptFoundation">Prompt Foundation</Label>
                  <textarea
                    id="promptFoundation"
                    className="w-full min-h-[300px] p-3 border border-gray-300 rounded-md resize-y"
                    placeholder="Enter prompt foundation using markdown..."
                    value={formData.promptFoundation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        promptFoundation: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            ) : (
              // View mode
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Model
                    </Label>
                    <div className="mt-2">
                      {company?.model ? (
                        <p className="text-sm text-gray-500 dark:text-gray-300">
                          {company.model}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-300 italic">
                          No model selected
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Voice
                    </Label>
                    <div className="mt-2">
                      {company?.voice ? (
                        <p className="text-sm text-gray-500 dark:text-gray-300">
                          {company.voice}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-300 italic">
                          No voice selected
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Assigned Tools
                    </Label>
                    <div className="mt-2">
                      {company?.assignedTools &&
                      company.assignedTools.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {company.assignedTools.map((tool, index) => (
                            <Badge key={index} variant="secondary">
                              {tool}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-300 italic">
                          No tools assigned
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Settings Display */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Settings
                    </Label>
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={company?.greetOnConnectEnabled ?? true}
                          disabled={true}
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          Enable greeting on connect
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      System Prompt
                    </Label>
                    {company?.systemPrompt && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setIsPreviewingSystemPrompt(!isPreviewingSystemPrompt)
                        }
                      >
                        {isPreviewingSystemPrompt ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-1" />
                            Raw
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                    {company?.systemPrompt ? (
                      isPreviewingSystemPrompt ? (
                        <MarkdownRenderer>
                          {company.systemPrompt}
                        </MarkdownRenderer>
                      ) : (
                        <div
                          className="text-sm font-mono max-w-full overflow-hidden"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                            overflowWrap: "anywhere",
                            maxWidth: "100%",
                          }}
                        >
                          {company.systemPrompt}
                        </div>
                      )
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-300 italic">
                        No system prompt configured. Click "Configure" to add
                        one.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      Prompt Foundation
                    </Label>
                    {company?.promptFoundation && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setIsPreviewingPromptFoundation(
                            !isPreviewingPromptFoundation
                          )
                        }
                      >
                        {isPreviewingPromptFoundation ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-1" />
                            Raw
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                    {company?.promptFoundation ? (
                      isPreviewingPromptFoundation ? (
                        <MarkdownRenderer>
                          {company.promptFoundation}
                        </MarkdownRenderer>
                      ) : (
                        <div
                          className="text-sm font-mono max-w-full overflow-hidden"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-all",
                            overflowWrap: "anywhere",
                            maxWidth: "100%",
                          }}
                        >
                          {company.promptFoundation}
                        </div>
                      )
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-300 italic">
                        No prompt foundation configured. Click "Configure" to
                        add one.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
